import createCachedSelector from 're-reselect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as api from '../../services/api';
import { SORT_BY_STRING } from '../../utils/sorting';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data) => data.storeKey,
  (data) => {
    const labelSorter = SORT_BY_STRING('label');

    const mappedClientBranchesLocations = (data || []).reduce((all, { branchId, locations }) => {
      all[branchId] = locations;
      return all;
    }, {});

    const mappedClientBranchesLocationsOptions = (data || []).reduce((all, { branchId, locations }) => {
      all[branchId] = locations
        .map(({ id, name, lat, lng }) => ({ value: id, label: name, lat, lng }))
        .sort(labelSorter);
      return all;
    }, {});

    return { mappedClientBranchesLocations, mappedClientBranchesLocationsOptions };
  }
)({
  keySelector: (data, storeKey) => storeKey
});

const useClientLocations = ({ clientId } = {}) => {
  const { accountId } = useAccount();
  const queryClient = useQueryClient();

  const storeKey = ['client-locations', accountId, clientId];
  const { isLoading, data } = useQuery(storeKey, () => api.fetchClientLocations({ accountId, clientId }), {
    staleTime: 0,
    enabled: !!clientId,
    select: (locationsData) => selector(locationsData, storeKey.join('#'))
  });

  const { mutateAsync: addClientLocation } = useMutation(
    ({ clientBranchId, location }) => api.addClientLocation({ accountId, clientId, clientBranchId, location }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mutateAsync: updateClientLocation } = useMutation(
    ({ id, clientBranchId, ...location }) =>
      api.updateClientLocation({ accountId, clientId, clientBranchId, locationId: id, location }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
      }
    }
  );

  const { mappedClientBranchesLocations, mappedClientBranchesLocationsOptions } = data || {};

  return {
    loading: isLoading,
    data,
    mappedClientBranchesLocations,
    mappedClientBranchesLocationsOptions,
    addClientLocation,
    updateClientLocation
  };
};

export default useClientLocations;
